import React, { useState } from "react"

import { Briefcase, Building, ChevronDown, ChevronRight, MapPin, Users } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import type { ApplicationDetails } from "@/hooks/useApplicationDetails"

interface JobEmploymentDetailsProps {
  job: ApplicationDetails["job"]
}

const JobEmploymentDetails = ({ job }: JobEmploymentDetailsProps) => {
  const [isOpen, setIsOpen] = useState(true)

  const employer = job?.employer
  const depotName = job?.depot?.name
  const dsp = job?.dsp
  const driverManagerTeam = job?.driver_manager_team
  const sheriffType = job?.sheriff_type
  const shiftAreas = job?.shift_areas

  const hasData =
    employer ||
    dsp ||
    driverManagerTeam ||
    sheriffType ||
    (shiftAreas && shiftAreas.length > 0) ||
    depotName

  if (!hasData) {
    return null
  }

  return (
    <Card className="shadow-sm">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-3">
                <Briefcase className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg font-semibold">Job & Employment Details</CardTitle>
              </div>
              {isOpen ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {employer && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-muted-foreground">Employer</label>
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{employer}</span>
                  </div>
                </div>
              )}

              {depotName && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-muted-foreground">Depot</label>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{depotName}</span>
                  </div>
                </div>
              )}

              {dsp && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-muted-foreground">DSP</label>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{dsp}</span>
                  </div>
                </div>
              )}

              {driverManagerTeam && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-muted-foreground">
                    Driver Manager Team
                  </label>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{driverManagerTeam}</span>
                  </div>
                </div>
              )}

              {sheriffType && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-muted-foreground">Sheriff Type</label>
                  <div>
                    <Badge variant="secondary">{sheriffType}</Badge>
                  </div>
                </div>
              )}

              {shiftAreas && shiftAreas.length > 0 && (
                <div className="space-y-1 md:col-span-2">
                  <label className="text-sm font-medium text-muted-foreground">Block Areas</label>
                  <div className="flex flex-wrap gap-2">
                    {shiftAreas.map((area, index) => (
                      <Badge key={index} variant="outline">
                        {area}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

export default JobEmploymentDetails
